import React, { createContext, useContext, useState, useEffect } from 'react';
import apiAuthService from '../services/apiAuthService';
import tokenService from '../services/tokenService';
import { safeConsole } from '../utils/productionUtils';

const AuthContext = createContext({});

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Function to handle user session setup
  const setupUserSession = (sessionData) => {
    if (sessionData?.user) {
      setUser(sessionData.user);
      
      // Store the tokens in our token service if they exist
      if (sessionData.access_token) {
        tokenService.saveTokens({
          accessToken: sessionData.access_token,
          refreshToken: sessionData.refresh_token,
          expiresAt: sessionData.expires_at
        });
      }
      return true;
    }
    return false;
  };

  useEffect(() => {
    // Check active sessions and sets the user
    const getInitialSession = async () => {
      try {
        if (process.env.NODE_ENV === 'development') {
          safeConsole.log("Checking for existing session...");
        }
        
        // First check if we have tokens in local storage
        const accessToken = tokenService.getAccessToken();
        
        if (accessToken) {
          if (process.env.NODE_ENV === 'development') {
            safeConsole.log("Found access token in storage, validating...");
          }
          
          // Try to validate the current token
          const validationResult = await tokenService.validateToken();
          
          if (validationResult.success) {
            if (process.env.NODE_ENV === 'development') {
              safeConsole.log("Token validation successful");
            }
            setUser(validationResult.user);
            setLoading(false);
            return;
          } 
          
          safeConsole.log("Token validation failed, attempting refresh");
          
          // Token validation failed, try to refresh
          const refreshResult = await tokenService.refreshToken();
          
          if (refreshResult.success) {
            safeConsole.log("Token refresh successful");
            setUser(refreshResult.user);
            setLoading(false);
            return;
          }
          
          safeConsole.log("Token refresh failed");
        }
        
        // If we get here, we need to check with the API
        safeConsole.log("Checking session with API");
        const { data, error } = await apiAuthService.getSession();
        
        if (error) {
          safeConsole.error("API session error:", error);
        } else if (data?.session) {
          safeConsole.log("Found session from API");
          setupUserSession(data.session);
        } else {
          safeConsole.log("No session found");
        }
      } catch (error) {
        safeConsole.error('Error checking auth session:', error);
      } finally {
        setLoading(false);
      }
    };

    getInitialSession();

    // Listen for changes on auth state (sign in, sign out, etc.)
    const { data: { subscription } } = apiAuthService.onAuthStateChange((event, session) => {
      safeConsole.log("Auth state change event:", event);
      
      if (session) {
        setupUserSession(session);
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        tokenService.clearTokens();
      }
      
      setLoading(false);
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  const value = {
    user,
    loading,
    signIn: (options) => apiAuthService.signInWithOAuth(options),
    signOut: async () => {
      try {
        // Use our apiAuthService service to sign out
        await apiAuthService.signOut();
        setUser(null);
      } catch (error) {
        safeConsole.error('Error signing out:', error);
      }
    },
    signUp: ({ email, password, options }) => 
      apiAuthService.signUpEmail({ 
        email, 
        password, 
        fullName: options?.data?.full_name 
      }),
    signUpWithUsername: async (username, password, fullName) => 
      apiAuthService.signUpUsername({ username, password, fullName }),
    signInWithEmail: async ({ email, password }) => {
      try {
        const data = await apiAuthService.signInEmail({ email, password });
        return { data, error: null };
      } catch (error) {
        return { data: null, error };
      }
    },
    signInWithUsername: async (username, password) => {
      try {
        const data = await apiAuthService.signInUsername({ username, password });
        return { data, error: null };
      } catch (error) {
        return { data: null, error };
      }
    },
    resetPassword: (email) => apiAuthService.resetPassword(email),
    updatePassword: async (password) => {
      try {
        await apiAuthService.updatePassword(password);
        return { error: null };
      } catch (error) {
        return { error };
      }
    },
    validateToken: () => tokenService.validateToken(),
    refreshToken: () => tokenService.refreshToken(),
    getAccessToken: () => tokenService.getAccessToken()
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext; 